"use client";

// Inside a package — /community/skills/<owner>/<repo>.
//
// ONE way to walk a folder of skills (2026-09-29, the folder rows are gone):
// the skills as the commons' own tiles in a row from the left edge (the shared
// Deck: swipe on glass, drag with a mouse, the arrow keys or the round steps).
// The card at the front has its whole document reading beneath the row, and
// it changes as you swipe, so a folder can be read through without opening a
// page at all. Where that document starts, the skill carries ITS OWN link bar
// and its own ledger (who wrote it, its version, where it sits in the repo),
// so the skill never reads as merely a row of the folder it came in.
// Either way a skill opens on its own page, whose crumb keeps saying where it
// sits: community / skills / <this folder> / <the skill>.

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { originName, type CommunitySkill } from "../../lib/community/types";
import type { SkillPack } from "../../lib/community/packs";
import { SkillCard, countUse } from "./SkillCard";
import SkillMarkdown from "../skills/SkillMarkdown";
import { LinkBar } from "../skills/PasteUrl";
import Deck from "./Deck";

/** a folder is walked one way now: as swipeable cards (2026-09-29). The type
 *  stays so an old `?view=folder` address still lands somewhere sensible. */
export type PackView = "folder" | "cards";

/** a skill's document, minus its manifest — read from its own /raw link. */
const bodies = new Map<string, Promise<string | null>>();
function bodyOf(s: CommunitySkill): Promise<string | null> {
  if (s.external) return Promise.resolve(null); // kept at its source: we only point at it
  let p = bodies.get(s.key);
  if (!p) {
    p = fetch(`${s.href}/raw`, { headers: { accept: "text/markdown" } })
      .then((r) => (r.ok ? r.text() : null))
      .then((t) => (t ? t.replace(/^\uFEFF?---\r?\n[\s\S]*?\r?\n---\r?\n?/, "").trim() : null))
      .catch(() => null);
    bodies.set(s.key, p);
  }
  return p;
}

/** where a skill sits inside its repo, when its source says so */
function pathInPack(s: CommunitySkill, pack: SkillPack): string | null {
  const u = s.sourceUrl ?? "";
  const m = new RegExp(`github\\.com/${pack.owner}/${pack.repo}/(?:tree|blob)/[^/]+/(.+?)/?$`, "i").exec(u);
  return m ? m[1] : null;
}

/** the front skill's document, read beneath the deck; it changes as you swipe. */
function DeckReader({ skill, pack, dir, copied, onCopy, memberName }: { skill: CommunitySkill; pack: SkillPack; dir: number; copied: boolean; onCopy: (s: CommunitySkill) => void; memberName?: string | null }) {
  const [body, setBody] = useState<{ key: string; text: string | null } | null>(null);
  useEffect(() => {
    let live = true;
    void bodyOf(skill).then((text) => { if (live) setBody({ key: skill.key, text }); });
    return () => { live = false; };
  }, [skill]);
  const ready = body?.key === skill.key;
  const where = pathInPack(skill, pack);
  const author = skill.by ?? originName(skill.origin);
  const brought = skill.broughtBy ? (memberName && skill.broughtBy.name === memberName ? "you" : skill.broughtBy.name) : null;
  return (
    <article
      key={skill.key}
      className="cm-deck__read"
      style={{ ["--enter-x" as string]: dir >= 0 ? "18px" : "-18px" } as React.CSSProperties}
      aria-live="polite"
    >
      <header className="cm-deck__read-head">
        <span className="cm-deck__read-cat">{skill.category}{skill.version ? ` · v${skill.version}` : ""}</span>
        <h2 className="cm-deck__read-t">{skill.title}</h2>
        <p className="cm-deck__read-d">{skill.description}</p>
        {/* THIS skill's own link, where the skill starts: not the folder's */}
        <div className="cm-deck__read-link">
          <LinkBar url={skill.pasteUrl} done={copied} onCopy={() => onCopy(skill)} label={`copy the link to ${skill.title}`} noswipe />
        </div>
        <div className="detail-rows cm-deck__read-ledger">
          <div className="detail-row"><span>written by</span><span>{author}</span></div>
          {brought && <div className="detail-row"><span>brought by</span><span>{brought}</span></div>}
          {skill.when_to_load && <div className="detail-row"><span>when to load it</span><span>{skill.when_to_load}</span></div>}
          {skill.version && <div className="detail-row"><span>version</span><span>{skill.version}</span></div>}
          {skill.updated && <div className="detail-row"><span>last tended</span><span>{skill.updated.slice(0, 10)}</span></div>}
          <div className="detail-row"><span>in the folder</span><span>{pack.owner}/{pack.repo}{where ? ` · ${where}` : ""}</span></div>
          {skill.uses && skill.uses.total > 0 && <div className="detail-row"><span>used</span><span>{skill.uses.total} {skill.uses.total === 1 ? "time" : "times"}</span></div>}
        </div>
        <span className="cm-deck__read-acts">
          {skill.external ? (
            <a className="quiet-link" href={skill.href} target="_blank" rel="noopener noreferrer">read it at its source</a>
          ) : (
            <Link className="quiet-link" href={skill.href} onClick={() => countUse(skill.key, "open")}>open its own page</Link>
          )}
        </span>
      </header>
      <div className="cm-deck__read-body" data-ready={ready || undefined}>
        {skill.external ? (
          <p className="sk-p">this one is kept at its source, under a licence that asks us not to copy it. hand your ai the link and it will read it there.</p>
        ) : !ready ? (
          <div className="cm-deck__read-wait" aria-hidden><span /><span /><span /></div>
        ) : body?.text ? (
          <SkillMarkdown body={body.text} />
        ) : (
          <p className="sk-p">the document could not be read just now. <Link className="quiet-link" href={skill.href}>open its page</Link> instead.</p>
        )}
      </div>
    </article>
  );
}

function CardDeck({
  pack,
  skills,
  copied,
  onCopy,
  memberName,
}: {
  pack: SkillPack;
  skills: CommunitySkill[];
  copied: string | null;
  onCopy: (s: CommunitySkill) => void;
  memberName?: string | null;
}) {
  const [at, setAt] = useState(0);
  const [dir, setDir] = useState(1);
  const onAt = useCallback((i: number, d: number) => { setAt(i); setDir(d); }, []);

  // the neighbours are read ahead, so a swipe never waits on the network
  useEffect(() => {
    for (const j of [at - 1, at + 1, at + 2]) if (skills[j]) void bodyOf(skills[j]);
  }, [at, skills]);

  const front = skills[Math.min(at, skills.length - 1)];
  return (
    <>
      <Deck
        count={skills.length}
        keyOf={(i) => skills[i].key}
        label="the skills in this folder. drag, swipe or use the arrow keys"
        noun="skill"
        onAt={onAt}
        renderCard={(i) => <SkillCard skill={skills[i]} copied={copied === skills[i].key} onCopy={onCopy} memberName={memberName} inFolder />}
      />
      {front && <DeckReader skill={front} pack={pack} dir={dir} copied={copied === front.key} onCopy={onCopy} memberName={memberName} />}
    </>
  );
}

export default function PackRoom({
  pack,
  skills,
  copied,
  onCopy,
  memberName,
}: {
  pack: SkillPack;
  /** kept for old callers; a folder is always cards now */
  view?: PackView;
  /** the skills of this folder the current search lets through */
  skills: CommunitySkill[];
  copied: string | null;
  onCopy: (s: CommunitySkill) => void;
  memberName?: string | null;
}) {
  if (skills.length === 0) return <p className="co-empty">nothing in this folder matches that yet.</p>;
  return <CardDeck pack={pack} skills={skills} copied={copied} onCopy={onCopy} memberName={memberName} />;
}
